import { useQuery, gql } from "@apollo/client";
import {
  BehaviorViolationFilterInput,
  BehaviorViolationSortInput,
  GetBehaviorViolationsQuery,
} from "../../gql/graphql";
import ShareParkTable, {
  usePaginationAndFilters,
} from "../../components/table/ShareParkTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ShareparkDateTime } from "../../components/LocalTime";
import React from "react";
import { Link } from "react-router-dom";

export const GET = gql`
  query GetBehaviorViolations(
    $where: BehaviorViolationFilterInput
    $take: Int
    $skip: Int
    $orderBy: [BehaviorViolationSortInput!]
  ) {
    behaviorViolations(
      where: $where
      take: $take
      skip: $skip
      order: $orderBy
    ) {
      totalCount
      items {
        id
        violationType
        detectedUtc
        escalationScheduledUtc
        escalationScheduledUtc
        resolvedUtc
        status
        description
        escalationNotes
        transaction {
          id
          startUtc
          parker {
            id
            firstName
            lastName
            fullName
          }
        }
        site {
          id
          name
        }
      }
    }
  }
`;

type BehaviorViolation = NonNullable<
  NonNullable<
    NonNullable<GetBehaviorViolationsQuery["behaviorViolations"]>["items"]
  >[number]
>;

const columnHelper = createColumnHelper<BehaviorViolation>();

const columns = [
  columnHelper.accessor("detectedUtc", {
    header: "Detected",
    cell: (info) => {
      return <ShareparkDateTime timestamp={info.getValue()} />;
    },
  }),
  columnHelper.accessor("site.name", {
    header: "Site",
    enableSorting: false,
    cell: (info) => {
      return info.getValue();
    },
  }),
  columnHelper.accessor("transaction.startUtc", {
    header: "Transaction",
    enableSorting: false,
    cell: (info) => {
      return (
        <Link to={`/transactions/${info.row.original?.transaction?.id}`}>
          <ShareparkDateTime timestamp={info.getValue()} />
        </Link>
      );
    },
  }),
  columnHelper.accessor("violationType", {
    header: "Violation Type",
  }),
  columnHelper.accessor("status", {
    header: "Status",
  }),
  columnHelper.accessor("escalationScheduledUtc", {
    header: "Escalation Scheduled",
    cell: (info) => {
      return <ShareparkDateTime timestamp={info.getValue()} />;
    },
  }),
];

export const BehaviourViolations = () => {
  const { pagination, setPagination, columnFilters, setColumnFilters } =
    usePaginationAndFilters();

  // Add sorting state
  const [sorting, setSorting] = React.useState([
    {
      id: "detectedUtc", // Default sorting column
      desc: true, // Default sorting direction (descending)
    },
  ]);

  const where = React.useMemo(() => {
    const where: BehaviorViolationFilterInput = {};
    for (const filter of columnFilters) {
      switch (filter.id) {
        case "site_name":
          if (filter.value != null) {
            where.site = { name: { contains: String(filter.value) } };
          }
          break;
        case "transaction_startUtc":
          if (filter.value != null) {
            where.transaction = where.transaction ?? {};
            where.transaction.startUtc = { gte: String(filter.value) };
          }
          break;
        case "transaction_parker.fullName":
          if (filter.value != null) {
            where.transaction = where.transaction ?? {};
            where.transaction.parker = {
              or: [
                { firstName: { contains: String(filter.value) } },
                { lastName: { contains: String(filter.value) } },
              ],
            };
          }
          break;
      }
    }
    return where;
  }, [columnFilters]);

  const orderBy = React.useMemo(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      return {
        [sort.id]: sort.desc ? "DESC" : "ASC",
      };
    }
    return undefined;
  }, [sorting]);

  const { data, loading } = useQuery<GetBehaviorViolationsQuery>(GET, {
    variables: {
      take: pagination.pageSize,
      skip: pagination.pageSize * pagination.pageIndex,
      where,
      orderBy: orderBy ? [orderBy] : undefined,
    },
    fetchPolicy: "no-cache",
  });

  return (
    <div className="border-top mt-3">
      <ShareParkTable
        data={data?.behaviorViolations ?? { items: [], totalCount: 0 }}
        columns={columns as ColumnDef<BehaviorViolation>[]}
        pagination={pagination}
        setPagination={setPagination}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        loading={loading}
        sorting={sorting}
        setSorting={setSorting}
      />
    </div>
  );
};
